import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth0 } from "@auth0/auth0-react";
import LanguageSelector from './Languages';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';
import CRMIntegration from '../Components/CRM/CRMIntegration';
import { GoogleCalendarIntegration } from '../Components/CalendarIntegration';
import { useNavigate } from 'react-router-dom';
import AIModelConfig from '../Components/AIModelConfig';
import { ProfileAssistantProvider, ProfileAssistantPanel, useProfileAssistant } from '../Components/ProfileAssistant';
import DocumentTemplates from '../Components/DocumentTemplates/DocumentTemplates';
import DeadlineReminderSettings from '../Components/DeadlineReminderSettings/DeadlineReminderSettings';
import IRCCAdmin from '../Components/IRCCAdmin/IRCCAdmin';
import PaymentSettings from '../Components/PaymentSettings';
import DocuSignIntegration from '../Components/DocuSignIntegration/DocuSignIntegration';
import SignatureTemplates from '../Components/SignatureTemplates/SignatureTemplates';
import { useToast } from '../Context/ToastContext';
import RightToolbar from '../Components/RightToolbar';
import { 
  BsKey, BsLink45Deg, BsCalendar3, BsRobot, BsFileText, BsClock, 
  BsGlobe, BsCodeSlash, BsBuilding, BsEnvelope, BsShare, BsPalette, 
  BsCalendarCheck, BsCreditCard, BsPeople, BsPerson, BsGear, BsBell,
  BsPen, BsShop
} from 'react-icons/bs';
import TeamManagement from '../Components/TeamManagement';
import MarketplaceSettings from '../Components/MarketplaceSettings/MarketplaceSettings';
import { fetchTaxonomy, findCategory, groupCategories, sanitizeSubcategories } from '../Data/advisoryTaxonomy';
import { useCapabilities } from '../Context/CapabilityContext';

// Weekdays for the structured working-hours editor (key = stored key, label = shown).
const WEEKDAYS = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];
const emptyAddress = () => ({ line1: '', line2: '', city: '', province: '', postalCode: '', country: '' });
const emptyWorkingHours = () => ({
  timezone: '',
  days: WEEKDAYS.reduce((acc, d) => { acc[d.key] = { closed: false, open: '', close: '' }; return acc; }, {}),
});
// Merge a stored workingHours object onto the empty default so every day key exists.
const hydrateWorkingHours = (wh) => {
  const base = emptyWorkingHours();
  if (!wh || typeof wh !== 'object') return base;
  base.timezone = wh.timezone || '';
  if (wh.days && typeof wh.days === 'object') {
    for (const d of WEEKDAYS) {
      const src = wh.days[d.key] || {};
      base.days[d.key] = { closed: !!src.closed, open: src.open || '', close: src.close || '' };
    }
  }
  return base;
};

// Nav items that only make sense for a specific discipline capability. An item id
// absent from this map is always shown; one present is shown only when its feature is on.
const NAV_ITEM_FEATURE = {
  ircc: 'irccForms',
  'programs-pipelines': 'immigrationPrograms',
  // A staffing agency's requisition board. Nobody else has requisitions, so nobody else
  // sees the item — the page refuses them too.
  positions: 'staffingPositions',
  'teach-program': 'immigrationPrograms',
};

// ============================================
// STYLED COMPONENTS - NEW LAYOUT
// ============================================

const PageWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  background-color: #0a0d12;
  overflow: hidden;
`;

const LeftSidebar = styled.div`
  width: 280px;
  min-width: 280px;
  height: 100%;
  background: linear-gradient(180deg, #0f1419 0%, #0a0d12 100%);
  border-right: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(99, 102, 241, 0.3) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 3px;
  }
`;

const SidebarHeader = styled.div`
  padding: 24px 20px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
`;

const ProfileCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const AvatarImage = styled.img`
  width: 52px;
  height: 52px;
  border-radius: 12px;
  border: 2px solid rgba(99, 102, 241, 0.4);
  object-fit: cover;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const AvatarFallback = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 12px;
  border: 2px solid rgba(99, 102, 241, 0.4);
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 18px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const UserDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.h3`
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserEmail = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
  margin: 2px 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NavSection = styled.div`
  padding: 16px 12px;
`;

const NavSectionTitle = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: rgba(255, 255, 255, 0.55);
  padding: 8px 12px;
  margin-bottom: 4px;
`;

const NavItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: none;
  border-radius: 10px;
  background: ${props => props.$active 
    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.15) 100%)' 
    : 'transparent'};
  border-left: 3px solid ${props => props.$active ? '#6366f1' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 2px;

  &:hover {
    background: ${props => props.$active 
      ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.15) 100%)'
      : 'rgba(99, 102, 241, 0.08)'};
  }
`;

const NavIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${props => props.$active ? '#a5b4fc' : 'rgba(255, 255, 255, 0.7)'};
  font-size: 14px;
  transition: color 0.2s ease;
`;

const NavLabel = styled.span`
  font-size: 13px;
  font-weight: ${props => props.$active ? '600' : '500'};
  color: ${props => props.$active ? '#fff' : 'rgba(255, 255, 255, 0.7)'};
  transition: color 0.2s ease;
`;

const NavBadge = styled.span`
  margin-left: auto;
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 10px;
  background: ${props => props.$color || 'rgba(99, 102, 241, 0.2)'};
  color: ${props => props.$textColor || 'rgba(255, 255, 255, 0.8)'};
  font-weight: 500;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ContentHeader = styled.div`
  padding: 24px 32px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
  background: rgba(15, 20, 25, 0.8);
  backdrop-filter: blur(10px);
`;

const ContentTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 4px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ContentSubtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
`;

const ContentBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 32px;
  scrollbar-width: thin;
  scrollbar-color: rgba(99, 102, 241, 0.3) transparent;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 4px;
  }
`;

const ContentCard = styled.div`
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.03) 100%);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
`;

const CardTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #818cf8;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  background: rgba(15, 20, 30, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 10px;
  padding: 12px 16px;
  color: #fff;
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.45);
  }

  &:read-only {
    background: rgba(10, 15, 20, 0.6);
    cursor: default;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  background: rgba(15, 20, 30, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 10px;
  padding: 12px 16px;
  color: #fff;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.45);
  }
`;

const Select = styled.select`
  width: 100%;
  background: rgba(15, 20, 30, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 10px;
  padding: 12px 16px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }

  option {
    background: #1a1f2e;
    color: #fff;
  }
`;

const FieldRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ActionButton = styled.button`
  background: ${props => props.$primary 
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
    : 'rgba(99, 102, 241, 0.15)'};
  color: #fff;
  border: ${props => props.$primary ? 'none' : '1px solid rgba(99, 102, 241, 0.3)'};
  border-radius: 10px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => props.$primary 
      ? '0 6px 20px rgba(99, 102, 241, 0.4)'
      : 'none'};
    background: ${props => props.$primary 
      ? 'linear-gradient(135deg, #5558e3 0%, #7c3aed 100%)'
      : 'rgba(99, 102, 241, 0.25)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const HelpText = styled.div`
  margin-top: 8px;
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.66);
  line-height: 1.5;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: rgba(15, 20, 30, 0.4);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 12px;
  margin-bottom: 12px;
`;

const ToggleLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ToggleLabelText = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  font-size: 14px;
`;

const ToggleLabelDescription = styled.span`
  color: rgba(255, 255, 255, 0.66);
  font-size: 12px;
  max-width: 400px;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
  flex-shrink: 0;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(99, 102, 241, 0.2);
    transition: 0.3s;
    border-radius: 26px;
    border: 1px solid rgba(99, 102, 241, 0.3);

    &:before {
      position: absolute;
      content: "";
      height: 20px;
      width: 20px;
      left: 2px;
      bottom: 2px;
      background-color: rgba(255, 255, 255, 0.8);
      transition: 0.3s;
      border-radius: 50%;
    }
  }

  input:checked + span {
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border-color: rgba(99, 102, 241, 0.5);
  }

  input:checked + span:before {
    transform: translateX(22px);
    background-color: white;
  }
`;

const StickyFooter = styled.div`
  padding: 16px 32px;
  background: rgba(15, 20, 25, 0.95);
  border-top: 1px solid rgba(99, 102, 241, 0.15);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-shrink: 0;
  min-height: 68px;
`;

const StatusPill = styled.span`
  font-size: 10px;
  padding: 4px 10px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(34, 197, 94, 0.1) 100%);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: rgba(34, 197, 94, 0.9);
  font-weight: 600;
`;

const SettingsButton = styled.button`
  width: 100%;
  background: rgba(0, 200, 81, 0.1);
  color: rgba(0, 200, 81, 0.9);
  border: 1px solid rgba(0, 200, 81, 0.3);
  border-radius: 10px;
  padding: 14px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover {
    background: rgba(0, 200, 81, 0.2);
    transform: translateY(-1px);
  }
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const IntegrationCard = styled.div`
  background: rgba(15, 20, 30, 0.4);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 12px;
  padding: 20px;
`;

// ============================================
// CONSTANTS
// ============================================

// Category + subcategory options come from the server-authoritative taxonomy
// (src/Data/advisoryTaxonomy.js -> GET /onboarding/taxonomy). No hardcoded list.
const SubcategoryChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
`;
const SubChip = styled.button`
  border: 1px solid ${(p) => (p.selected ? '#2563eb' : 'rgba(165, 180, 252, 0.45)')};
  background: ${(p) => (p.selected ? '#2563eb' : 'rgba(99, 102, 241, 0.08)')};
  color: ${(p) => (p.selected ? '#fff' : 'rgba(255, 255, 255, 0.88)')};
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { border-color: #2563eb; background: ${(p) => (p.selected ? '#2563eb' : 'rgba(99, 102, 241, 0.18)')}; }
`;

const COLOR_SCHEME_OPTIONS = [
  'cyan', 'darkblue', 'lightblue', 'red', 'orange', 'magenta', 'green', 'yellow'
];

// Navigation categories
const NAV_SECTIONS = [
  {
    title: 'Account',
    items: [
      { id: 'profile', label: 'Profile', icon: BsPerson },
      { id: 'business', label: 'Business Info', icon: BsBuilding },
      { id: 'contact', label: 'Contact', icon: BsEnvelope },
      { id: 'social', label: 'Social Media', icon: BsShare },
    ]
  },
  {
    title: 'Firm Settings',
    items: [
      { id: 'programs-pipelines', label: 'Programs & Pipelines', icon: BsGear },
      { id: 'positions', label: 'Positions & Employers', icon: BsGear },
      { id: 'teach-program', label: 'Teach the Brain a Program', icon: BsGear },
    ]
  },
  {
    title: 'Integrations',
    items: [
      { id: 'api', label: 'API Key', icon: BsKey },
      { id: 'crm', label: 'CRM', icon: BsLink45Deg },
      { id: 'calendar', label: 'Calendar', icon: BsCalendar3 },
      { id: 'esignature', label: 'E-Signature', icon: BsPen },
      { id: 'ai', label: 'AI Model', icon: BsRobot },
    ]
  },
  {
    title: 'Widget',
    items: [
      { id: 'widget-access', label: 'Share Widget', icon: BsGlobe },
      { id: 'widget-embed', label: 'Embed Code', icon: BsCodeSlash },
      { id: 'widget-style', label: 'Customization', icon: BsPalette },
    ]
  },
  {
    title: 'Marketplace',
    items: [
      { id: 'marketplace', label: 'Public Listing', icon: BsShop },
    ]
  },
  {
    title: 'Forms',
    items: [
      { id: 'templates', label: 'Templates', icon: BsFileText },
      { id: 'ircc', label: 'IRCC Forms', icon: BsFileText, badge: '🍁' },
    ]
  },
  {
    title: 'Settings',
    items: [
      { id: 'team', label: 'Team', icon: BsPeople },
      { id: 'meetings', label: 'Post-Conversation', icon: BsCalendarCheck },
      { id: 'payments', label: 'Payments', icon: BsCreditCard },
      { id: 'meeting-reminders', label: 'Meeting Reminders', icon: BsBell },
      { id: 'deadline-reminders', label: 'Deadline Reminders', icon: BsClock },
      { id: 'preferences', label: 'Preferences', icon: BsGear },
    ]
  }
];

// ============================================
// SECTION CONTENT COMPONENTS
// ============================================

const SectionDescriptions = {
  profile: 'View and manage your account profile',
  business: 'Configure your business details',
  contact: 'Manage your contact information',
  social: 'Link your social media accounts',
  api: 'Manage your API key for integrations',
  crm: 'Connect to Monday.com and other CRMs',
  calendar: 'Sync with Google Calendar',
  esignature: 'Connect to DocuSign and manage signature templates',
  ai: 'Configure AI model parameters',
  'widget-access': 'Share your AI assistant with clients',
  'widget-embed': 'Embed the widget on your website',
  'widget-style': 'Customize your widget appearance',
  marketplace: 'How you appear to visitors on imavenai.com — languages, countries, price, terms',
  templates: 'Manage PDF form templates',
  ircc: 'Manage IRCC form URLs and sync',
  team: 'Manage your team members',
  meetings: 'Configure what happens after a widget conversation',
  payments: 'Configure how you receive payments',
  'meeting-reminders': 'Configure meeting reminder emails',
  'deadline-reminders': 'Configure deadline/expiration reminder emails',
  preferences: 'Language and timezone settings',
};

// ============================================
// MEETING REMINDER SETTINGS COMPONENT
// ============================================

const MeetingReminderSettingsSection = ({ apiClient, success, showError }) => {
  const [settings, setSettings] = useState({
    enabled: true,
    reminderHoursBeforeMeeting: 5,
    sendReminderToUser: true,
    sendReminderToAdviser: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await apiClient.get('/profile/meeting-reminders');
        if (response.data?.meetingReminderSettings) {
          setSettings(response.data.meetingReminderSettings);
        }
      } catch (err) {
        console.error('Error fetching meeting reminder settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [apiClient]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/profile/meeting-reminders', {
        meetingReminderSettings: settings
      });
      success('Meeting reminder settings saved!');
    } catch (err) {
      console.error('Error saving meeting reminder settings:', err);
      showError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <HelpText>Loading settings...</HelpText>;
  }

  return (
    <>
      <ToggleContainer>
        <ToggleLabel>
          <ToggleLabelText>Enable Meeting Reminders</ToggleLabelText>
          <ToggleLabelDescription>
            Send email reminders before scheduled meetings
          </ToggleLabelDescription>
        </ToggleLabel>
        <ToggleSwitch>
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
          />
          <span></span>
        </ToggleSwitch>
      </ToggleContainer>

      <InputGroup>
        <Label>Send Reminder Hours Before Meeting</Label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Input
            type="number"
            min="1"
            max="48"
            value={settings.reminderHoursBeforeMeeting}
            onChange={(e) => {
              const value = parseInt(e.target.value) || 1;
              const clampedValue = Math.min(48, Math.max(1, value));
              setSettings({ ...settings, reminderHoursBeforeMeeting: clampedValue });
            }}
            disabled={!settings.enabled}
            style={{ width: '100px' }}
          />
          <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
            hour{settings.reminderHoursBeforeMeeting !== 1 ? 's' : ''} before meeting
          </span>
        </div>
        <HelpText>
          Enter a value between 1 and 48 hours. Reminder emails will be sent this many hours before the scheduled meeting time.
        </HelpText>
      </InputGroup>

      <ToggleContainer style={{ opacity: settings.enabled ? 1 : 0.5 }}>
        <ToggleLabel>
          <ToggleLabelText>Send Reminder to Client</ToggleLabelText>
          <ToggleLabelDescription>
            Send meeting reminder to the user/client
          </ToggleLabelDescription>
        </ToggleLabel>
        <ToggleSwitch>
          <input
            type="checkbox"
            checked={settings.sendReminderToUser}
            onChange={(e) => setSettings({ ...settings, sendReminderToUser: e.target.checked })}
            disabled={!settings.enabled}
          />
          <span></span>
        </ToggleSwitch>
      </ToggleContainer>

      <ToggleContainer style={{ opacity: settings.enabled ? 1 : 0.5 }}>
        <ToggleLabel>
          <ToggleLabelText>Send Reminder to Yourself</ToggleLabelText>
          <ToggleLabelDescription>
            Send meeting reminder to you (the adviser)
          </ToggleLabelDescription>
        </ToggleLabel>
        <ToggleSwitch>
          <input
            type="checkbox"
            checked={settings.sendReminderToAdviser}
            onChange={(e) => setSettings({ ...settings, sendReminderToAdviser: e.target.checked })}
            disabled={!settings.enabled}
          />
          <span></span>
        </ToggleSwitch>
      </ToggleContainer>

      <div style={{ marginTop: '20px' }}>
        <ActionButton $primary onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Reminder Settings'}
        </ActionButton>
      </div>
    </>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const AutoReviewToggle = ({ apiClient }) => {
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!apiClient) return;
    apiClient.get('/profile').then(res => {
      const profile = res.data?.profile || res.data;
      setEnabled(!!profile?.autoConversationReview);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [apiClient]);

  const toggle = async () => {
    const newVal = !enabled;
    setEnabled(newVal);
    setSaving(true);
    try {
      await apiClient.put('/profile', { profile: { autoConversationReview: newVal } });
    } catch {
      try { await apiClient.put('/profile/update', { profile: { autoConversationReview: newVal } }); } catch {}
    }
    setSaving(false);
  };

  if (!loaded) return null;

  return (
    <InputGroup>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0' }}>
        <div>
          <Label style={{ marginBottom: 4 }}>Auto-review conversations after completion</Label>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', margin: 0 }}>
            AI will automatically review finished conversations and create teachings
          </p>
        </div>
        <div
          onClick={toggle}
          style={{
            width: 44, height: 24, borderRadius: 12,
            background: enabled ? 'rgba(168, 85, 247, 0.6)' : 'rgba(255,255,255,0.15)',
            cursor: saving ? 'wait' : 'pointer',
            position: 'relative', transition: 'background 0.2s',
            flexShrink: 0
          }}
        >
          <div style={{
            width: 18, height: 18, borderRadius: '50%',
            background: '#fff', position: 'absolute', top: 3,
            left: enabled ? 23 : 3, transition: 'left 0.2s'
          }} />
        </div>
      </div>
    </InputGroup>
  );
};

const IMAVEN_HOME_URL = 'https://imavenai.com/';
// Only an imavenai.com https URL with no characters that could break out of the href attribute.
const isSafePublicUrl = (url) =>
  typeof url === 'string' && /^https:\/\/imavenai\.com\/[^\s"'<>`]*$/.test(url);

const ProfilePage = () => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const apiClient = useAuthenticatedAxios();
  const navigate = useNavigate();
  const { success, error: showError, info } = useToast();

  const [activeSection, setActiveSection] = useState(
    () => new URLSearchParams(window.location.search).get('section') || 'profile'
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  // Target of the "Powered by iMaven" line in the embed snippet: the adviser's public page while
  // listed in the marketplace, else the site home. Re-read whenever the Embed Code tab opens.
  const [publicPage, setPublicPage] = useState({ url: IMAVEN_HOME_URL, listed: false });

  const queryParams = new URLSearchParams(window.location.search);
  const useMockData = queryParams.get('mock') === 'true';

  const [taxonomy, setTaxonomy] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetchTaxonomy().then((cats) => { if (!cancelled) setTaxonomy(cats); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (activeSection !== 'widget-embed' || !isAuthenticated || useMockData) return undefined;
    let cancelled = false;
    apiClient.get('/marketplace/me/public-page')
      .then((res) => {
        if (cancelled) return;
        const url = res?.data?.url;
        setPublicPage(isSafePublicUrl(url)
          ? { url, listed: !!res.data.listed }
          : { url: IMAVEN_HOME_URL, listed: false });
      })
      .catch(() => { if (!cancelled) setPublicPage({ url: IMAVEN_HOME_URL, listed: false }); });
    return () => { cancelled = true; };
  }, [activeSection, isAuthenticated, apiClient, useMockData]);

  // Discipline capabilities — hide immigration-specific nav for non-immigration experts.
  const { features: capFeatures, content: capContent } = useCapabilities();
  const hasAnySpecializedTool = Object.values(capFeatures).some(Boolean);
  const visibleNavSections = NAV_SECTIONS
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        const feat = NAV_ITEM_FEATURE[item.id];
        return !feat || capFeatures[feat];
      }),
    }))
    .filter((section) => section.items.length > 0);

  const [profileData, setProfileData] = useState({
    apiKey: '',
    businessInfo: {
      businessName: '',
      companyName: '',
      location: '',
      website: '',
      industry: '',
      subcategories: [],
      about: '',
      imageUrl: '',
      responseTime: '',
      score: 0,
      hourClose: '',
      address: emptyAddress(),
      workingHours: emptyWorkingHours(),
      greetingsMessage: ''
    },
    contact: {
      email: user?.email || '',
      phone: '',
      position: ''
    },
    socialMedia: {
      linkedin: '',
      facebook: '',
      twitter: '',
      instagram: ''
    },
    preferences: {
      language: 'English',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      notifications: { email: true, push: true, messages: true }
    },
    settings: {
      theme: 'dark',
      accessibility: { fontSize: 'normal', contrast: 'normal' }
    },
    widget: {
      colorScheme: 'lightblue',
      logoUrl: 'https://ststic-imaven-images.nyc3.cdn.digitaloceanspaces.com/bluelogo.svg'
    },
    meetingSettings: {
      allowMeetingsFromWidget: true,
      allowManualMeetings: true,
      allowAiOverviewFromWidget: true,
      aiAnalysisPrice: 10
    }
  });

  // Data fetching effect
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!isAuthenticated) return;
      setLoading(true);
      try {
        let profileData = {};
        let languageData = {};
        let calendarData = {};

        if (useMockData) {
          profileData = {
            businessName: "Sample Business",
            business_name: "Sample Business",
            location: "New York, NY",
            website: "https://example.com",
            industry: "Technology",
            email: user?.email || "sample@example.com",
            phone: "123-456-7890",
            position: "CEO",
            about: "Sample business description",
            imageUrl: "path_to_image",
            responseTime: "4 hours",
            score: 4.5,
            hourClose: "5:00pm",
            social: {
              linkedin: "https://linkedin.com/in/sample",
              facebook: "https://facebook.com/sample",
              twitter: "https://twitter.com/sample",
              instagram: "https://instagram.com/sample"
            },
            theme: "dark",
            notifications: { email: true, push: true, messages: true }
          };
          languageData = { language: "en" };
          calendarData = { timezone: "America/New_York" };
        } else {
          try {
            const profileResponse = await apiClient.get('/profile');
            profileData = profileResponse.data || {};
          } catch (err) {
            console.error("Error fetching profile data:", err.message);
            try {
              const directProfileResponse = await apiClient.get('/profile/by-id/6805e9290b79b070c30ccf44');
              profileData = directProfileResponse.data || {};
            } catch (directErr) {
              console.error("Error with direct profile endpoint:", directErr.message);
            }
          }
          try {
            const calendarResponse = await apiClient.get('/calander/UserCalanderSettings');
            calendarData = calendarResponse.data || {};
          } catch (err) {
            console.error("Error fetching calendar settings:", err.message);
          }
        }

        setProfileData(prevData => {
          const profileObj = profileData.profile || profileData;
          const socialMedia = profileObj.info?.socialMedia || {};
          return {
            apiKey: profileObj.apiKey || '',
            businessInfo: {
              businessName: profileObj.name || '',
              companyName: profileObj.info?.companyName || '',
              location: profileObj.info?.location || '',
              website: profileObj.info?.website || profileObj.info?.site || '',
              industry: profileObj.userCategories || profileObj.info?.industry || '',
              subcategories: Array.isArray(profileObj.subcategories) ? profileObj.subcategories : [],
              about: profileObj.about || '',
              imageUrl: profileObj.imageUrl || profileObj.info?.image || '',
              responseTime: profileObj.responseTime || '',
              score: profileObj.score || 0,
              hourClose: profileObj.info?.hoursClose || profileObj.info?.hourClose || '',
              address: { ...emptyAddress(), ...(profileObj.info?.address || {}) },
              workingHours: hydrateWorkingHours(profileObj.info?.workingHours),
              greetingsMessage: profileObj.greetingsMessage || ''
            },
            contact: {
              email: profileObj.info?.email || user?.email || '',
              phone: profileObj.info?.phone || '',
              position: profileObj.info?.position || ''
            },
            socialMedia: {
              linkedin: socialMedia.linkedin || '',
              facebook: socialMedia.facebook || '',
              twitter: socialMedia.twitter || '',
              instagram: socialMedia.instagram || ''
            },
            preferences: {
              language: languageData.language || 'en',
              timezone: calendarData.Timezone || 'UTC',
              notifications: { email: true, push: true, messages: true }
            },
            settings: {
              theme: 'dark',
              accessibility: { fontSize: 'normal', contrast: 'normal' }
            },
            widget: {
              colorScheme: profileObj.widget?.colorScheme || profileObj.headerColor || 'lightblue',
              logoUrl: profileObj.widget?.logoUrl || profileObj.logoUrl || profileObj.imageUrl || 'https://ststic-imaven-images.nyc3.cdn.digitaloceanspaces.com/bluelogo.svg'
            },
            meetingSettings: {
              allowMeetingsFromWidget: profileObj.meetingSettings?.allowMeetingsFromWidget ?? true,
              allowManualMeetings: profileObj.meetingSettings?.allowManualMeetings ?? true,
              allowAiOverviewFromWidget: profileObj.meetingSettings?.allowAiOverviewFromWidget ?? true,
              aiAnalysisPrice: profileObj.meetingSettings?.aiAnalysisPrice ?? 10
            },
            headerColor: profileObj.widget?.colorScheme || 'lightblue',
            logoUrl: profileObj.imageUrl || 'https://ststic-imaven-images.nyc3.cdn.digitaloceanspaces.com/bluelogo.svg'
          };
        });
        setError(null);
      } catch (err) {
        console.error("Error in profile data fetching process:", err);
        setError(err.message || "Failed to fetch profile data");
      } finally {
        setLoading(false);
      }
    };
    fetchProfileData();
  }, [isAuthenticated, user, apiClient, useMockData]);

  const handleInputChange = (section, field, value) => {
    setProfileData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const selectedCategory = findCategory(taxonomy, profileData.businessInfo.industry);

  // Changing category drops any subcategory selections no longer valid for it.
  const handleCategoryChange = (label) => {
    const cat = findCategory(taxonomy, label);
    setProfileData(prev => ({
      ...prev,
      businessInfo: {
        ...prev.businessInfo,
        industry: label,
        subcategories: sanitizeSubcategories(cat, prev.businessInfo.subcategories),
      },
    }));
  };

  const toggleSubcategory = (slug) => {
    setProfileData(prev => {
      const multi = selectedCategory?.subMulti !== false;
      const current = prev.businessInfo.subcategories || [];
      const has = current.includes(slug);
      let next;
      if (multi) {
        next = has ? current.filter(s => s !== slug) : [...current, slug];
      } else {
        next = has ? [] : [slug];
      }
      return { ...prev, businessInfo: { ...prev.businessInfo, subcategories: next } };
    });
  };

  // Structured address + working-hours editors (nested under businessInfo).
  const handleAddressChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      businessInfo: { ...prev.businessInfo, address: { ...prev.businessInfo.address, [field]: value } },
    }));
  };
  const handleWorkingHoursTz = (value) => {
    setProfileData(prev => ({
      ...prev,
      businessInfo: { ...prev.businessInfo, workingHours: { ...prev.businessInfo.workingHours, timezone: value } },
    }));
  };
  const handleWorkingDayChange = (dayKey, field, value) => {
    setProfileData(prev => ({
      ...prev,
      businessInfo: {
        ...prev.businessInfo,
        workingHours: {
          ...prev.businessInfo.workingHours,
          days: {
            ...prev.businessInfo.workingHours.days,
            [dayKey]: { ...prev.businessInfo.workingHours.days[dayKey], [field]: value },
          },
        },
      },
    }));
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      success(`${label} copied to clipboard`);
    } catch (err) {
      showError(`Failed to copy ${label}`);
    }
  };

  const getWidgetUrl = () => {
    if (!profileData.apiKey) return '';
    return `https://adviser.imavenai.com/chat/${encodeURIComponent(profileData.apiKey)}`;
  };

  // Plain, visible HTML on the adviser's own page (not injected by the widget script, not inside the
  // widget iframe), brand-only anchor text, no redirect: the adviser's editorial choice, free to remove.
  const getPoweredByLine = () =>
    `<p style="font-size:12px;text-align:center;margin:8px 0;"><a href="${publicPage.url}">Powered by iMaven</a></p>`;

  const getEmbedSnippet = () => {
    if (!profileData.apiKey) return '';
    const keyPart = encodeURIComponent(profileData.apiKey);
    return `<link rel="stylesheet" type="text/css" href="https://widget-server-5q5aq.ondigitalocean.app/files/widget.css?api_key=${keyPart}">
<script src="https://widget-server-5q5aq.ondigitalocean.app/files/widget?api_key=${keyPart}"></script>
${getPoweredByLine()}`;
  };

  const getPlaceholderSnippet = () => {
    return `<link rel="stylesheet" type="text/css" href="https://widget-server-5q5aq.ondigitalocean.app/files/widget.css?api_key=YOUR_API_KEY_HERE">
<script src="https://widget-server-5q5aq.ondigitalocean.app/files/widget?api_key=YOUR_API_KEY_HERE"></script>
${getPoweredByLine()}`;
  };

  const shareWidgetUrl = async () => {
    const url = getWidgetUrl();
    if (!url) return;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'iMaven AI Personal Widget', text: 'Here is my iMaven AI widget link:', url });
      } else {
        await navigator.clipboard.writeText(url);
        info('Share not supported on this device. Link copied to clipboard.');
      }
    } catch (err) {
      showError('Share failed');
    }
  };

  const handleSave = async () => {
    try {
      const profilePayload = {
        profile: {
          name: profileData.businessInfo.businessName,
          userCategories: profileData.businessInfo.industry,
          subcategories: profileData.businessInfo.subcategories || [],
          about: profileData.businessInfo.about,
          imageUrl: profileData.businessInfo.imageUrl,
          responseTime: profileData.businessInfo.responseTime,
          score: profileData.businessInfo.score,
          greetingsMessage: profileData.businessInfo.greetingsMessage,
          widget: {
            colorScheme: profileData.widget.colorScheme || 'lightblue',
            logoUrl: profileData.widget.logoUrl || 'https://ststic-imaven-images.nyc3.cdn.digitaloceanspaces.com/bluelogo.svg'
          },
          info: {
            location: profileData.businessInfo.location,
            website: profileData.businessInfo.website,
            email: profileData.contact.email,
            phone: profileData.contact.phone,
            position: profileData.contact.position,
            hourClose: profileData.businessInfo.hourClose,
            companyName: profileData.businessInfo.companyName,
            address: profileData.businessInfo.address,
            workingHours: profileData.businessInfo.workingHours,
            socialMedia: {
              linkedin: profileData.socialMedia.linkedin,
              facebook: profileData.socialMedia.facebook,
              twitter: profileData.socialMedia.twitter,
              instagram: profileData.socialMedia.instagram
            },
            headerColor: profileData.widget.colorScheme || 'lightblue',
            logoUrl: profileData.imageUrl || 'https://ststic-imaven-images.nyc3.cdn.digitaloceanspaces.com/bluelogo.svg'
          }
        }
      };

      if (useMockData) {
        success('Profile saved successfully! (Mock)');
        return;
      }

      try {
        await apiClient.put('/profile', profilePayload);
      } catch (err) {
        try {
          await apiClient.put('/profile/update', profilePayload);
        } catch (altErr) {
          console.error("Error with profile endpoint:", altErr.message);
        }
      }

      try {
        await apiClient.put('/profile/social-media', {
          linkedin: profileData.socialMedia.linkedin,
          facebook: profileData.socialMedia.facebook,
          twitter: profileData.socialMedia.twitter,
          instagram: profileData.socialMedia.instagram
        });
      } catch (err) {
        console.error("Error saving social media:", err.message);
      }

      try {
        await apiClient.put('/calander/UserCalanderSettings', { Timezone: profileData.preferences.timezone });
      } catch (err) {
        console.error("Error saving calendar settings:", err.message);
      }

      try {
        await apiClient.put('/profile/meeting-settings', {
          meetingSettings: {
            allowMeetingsFromWidget: profileData.meetingSettings.allowMeetingsFromWidget,
            allowManualMeetings: profileData.meetingSettings.allowManualMeetings,
            allowAiOverviewFromWidget: profileData.meetingSettings.allowAiOverviewFromWidget,
            aiAnalysisPrice: profileData.meetingSettings.aiAnalysisPrice
          }
        });
      } catch (err) {
        console.error("Error saving meeting settings:", err.message);
      }

      success('Profile saved successfully!');
    } catch (err) {
      console.error("Critical error in save process:", err);
      showError(`Failed to save profile: ${err.message}`);
    }
  };

  // Get current section title
  const getCurrentSectionTitle = () => {
    for (const section of NAV_SECTIONS) {
      const item = section.items.find(i => i.id === activeSection);
      if (item) return item.label;
    }
    return 'Settings';
  };

  // Render section content
  const renderSectionContent = () => {
    switch (activeSection) {
      case 'programs-pipelines':
        // Redirect to dedicated page
        navigate('/settings/programs-pipelines');
        return (
          <ContentCard>
            <CardTitle><BsGear /> Programs & Pipelines</CardTitle>
            <HelpText>Redirecting to Programs & Pipelines management...</HelpText>
          </ContentCard>
        );

      case 'positions':
        // Redirect to dedicated page
        navigate('/settings/positions');
        return (
          <ContentCard>
            <CardTitle><BsGear /> Positions & Employers</CardTitle>
            <HelpText>Redirecting to your positions board...</HelpText>
          </ContentCard>
        );

      case 'teach-program':
        // Redirect to dedicated page
        navigate('/settings/teach-program');
        return (
          <ContentCard>
            <CardTitle><BsGear /> Teach the Brain a Program</CardTitle>
            <HelpText>Redirecting to program teaching...</HelpText>
          </ContentCard>
        );

      case 'profile':
        return (
          <>
            <ContentCard>
              <CardTitle><BsPerson /> Account Profile</CardTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '24px' }}>
                {(user?.picture || profileData.businessInfo.imageUrl) ? (
                  <AvatarImage src={user?.picture || profileData.businessInfo.imageUrl} alt="Profile" style={{ width: '80px', height: '80px' }} />
                ) : (
                  <AvatarFallback style={{ width: '80px', height: '80px', fontSize: '28px' }}>
                    {(user?.name || user?.email || 'U').slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                )}
                <div>
                  <UserName style={{ fontSize: '18px', marginBottom: '4px' }}>
                    {profileData.businessInfo.businessName || user?.name || 'User'}
                  </UserName>
                  <UserEmail style={{ fontSize: '14px' }}>{user?.email}</UserEmail>
                  {profileData.apiKey && <StatusPill style={{ marginTop: '8px', display: 'inline-block' }}>API Linked</StatusPill>}
                </div>
              </div>
              <InputGroup>
                <Label>Display Name</Label>
                <Input
                  type="text"
                  value={profileData.businessInfo.businessName}
                  onChange={(e) => handleInputChange('businessInfo', 'businessName', e.target.value)}
                  placeholder="Your display name"
                />
              </InputGroup>
              <InputGroup>
                <Label>Profile Image URL</Label>
                <Input
                  type="url"
                  value={profileData.businessInfo.imageUrl}
                  onChange={(e) => handleInputChange('businessInfo', 'imageUrl', e.target.value)}
                  placeholder="https://example.com/image.jpg"
                />
              </InputGroup>
            </ContentCard>

            {(capFeatures.firmStandards || capFeatures.immigrationPrograms) && (
              <ContentCard>
                <CardTitle><BsGear /> Firm Configuration</CardTitle>
                {capFeatures.firmStandards && (
                  <>
                    <SettingsButton onClick={() => navigate('/settings/firm-standards')}>
                      <span>⚙️</span> Firm Standards
                    </SettingsButton>
                    <HelpText>
                      Configure your firm's policies, risk posture, and operating procedures for immigration cases.
                    </HelpText>
                  </>
                )}
                {capFeatures.immigrationPrograms && (
                  <>
                    <SettingsButton onClick={() => navigate('/settings/programs-pipelines')} style={{ marginTop: '12px' }}>
                      <span>📋</span> Programs & Pipelines
                    </SettingsButton>
                    <HelpText>
                      Manage which immigration programs your firm offers and their associated workflow pipelines.
                    </HelpText>
                  </>
                )}
              </ContentCard>
            )}
          </>
        );

      case 'business':
        return (
          <>
          <ContentCard>
            <CardTitle><BsBuilding /> Business Information</CardTitle>
            <GridRow>
              <InputGroup>
                <Label>Business Name</Label>
                <Input
                  type="text"
                  value={profileData.businessInfo.businessName}
                  onChange={(e) => handleInputChange('businessInfo', 'businessName', e.target.value)}
                />
              </InputGroup>
              <InputGroup>
                <Label>Legal / firm name <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.6)' }}>(optional)</span></Label>
                <Input
                  type="text"
                  value={profileData.businessInfo.companyName}
                  onChange={(e) => handleInputChange('businessInfo', 'companyName', e.target.value)}
                  placeholder="e.g., ACME Immigration Law LLP"
                />
              </InputGroup>
            </GridRow>
            <InputGroup>
              <Label>About</Label>
              <TextArea
                value={profileData.businessInfo.about}
                onChange={(e) => handleInputChange('businessInfo', 'about', e.target.value)}
                placeholder="Enter a description of your business"
              />
            </InputGroup>
            <InputGroup>
              <Label>Greeting Message</Label>
              <TextArea
                value={profileData.businessInfo.greetingsMessage}
                onChange={(e) => handleInputChange('businessInfo', 'greetingsMessage', e.target.value)}
                placeholder="e.g., Hello! How can I assist you today?"
              />
            </InputGroup>
            <GridRow>
              <InputGroup>
                <Label>Location</Label>
                <Input
                  type="text"
                  value={profileData.businessInfo.location}
                  onChange={(e) => handleInputChange('businessInfo', 'location', e.target.value)}
                />
              </InputGroup>
              <InputGroup>
                <Label>Website</Label>
                <Input
                  type="url"
                  value={profileData.businessInfo.website}
                  onChange={(e) => handleInputChange('businessInfo', 'website', e.target.value)}
                />
              </InputGroup>
            </GridRow>
            <GridRow>
              <InputGroup>
                <Label>Business Hours Close</Label>
                <Input
                  type="text"
                  value={profileData.businessInfo.hourClose}
                  onChange={(e) => handleInputChange('businessInfo', 'hourClose', e.target.value)}
                  placeholder="e.g., 5:00pm"
                />
              </InputGroup>
              <InputGroup>
                <Label>Response Time</Label>
                <Input
                  type="text"
                  value={profileData.businessInfo.responseTime}
                  onChange={(e) => handleInputChange('businessInfo', 'responseTime', e.target.value)}
                  placeholder="e.g., 4 hours"
                />
              </InputGroup>
            </GridRow>
            <InputGroup>
              <Label>Category <span style={{ color: '#ff4d4d' }}>*</span></Label>
              <Select
                value={profileData.businessInfo.industry}
                onChange={e => handleCategoryChange(e.target.value)}
                required
              >
                <option value="" disabled>Select a category…</option>
                {groupCategories(taxonomy).map(({ group, categories }) => (
                  <optgroup key={group} label={group}>
                    {categories.map(cat => (
                      <option key={cat.slug} value={cat.label}>{cat.label}</option>
                    ))}
                  </optgroup>
                ))}
                {/* Preserve a legacy free-text category not present in the taxonomy */}
                {profileData.businessInfo.industry && !selectedCategory && (
                  <option value={profileData.businessInfo.industry}>
                    {profileData.businessInfo.industry}
                  </option>
                )}
              </Select>
            </InputGroup>
            {selectedCategory && selectedCategory.subcategories?.length > 0 && (
              <InputGroup>
                <Label>
                  {selectedCategory.subLabel}
                  {selectedCategory.subRequired && <span style={{ color: '#ff4d4d' }}>*</span>}
                  {selectedCategory.subMulti && (
                    <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.6)', marginLeft: 6 }}>
                      (select all that apply)
                    </span>
                  )}
                </Label>
                <SubcategoryChips>
                  {selectedCategory.subcategories.map(sub => (
                    <SubChip
                      type="button"
                      key={sub.slug}
                      selected={(profileData.businessInfo.subcategories || []).includes(sub.slug)}
                      onClick={() => toggleSubcategory(sub.slug)}
                    >
                      {sub.label}
                    </SubChip>
                  ))}
                </SubcategoryChips>
              </InputGroup>
            )}
          </ContentCard>

          {/* Structured business address — shown to connected advisers on the Network page */}
          <ContentCard>
            <CardTitle><BsBuilding /> Business Address</CardTitle>
            <HelpText>Shown to advisers you're connected with on the Network page. Leave blank to hide.</HelpText>
            <InputGroup>
              <Label>Address line 1</Label>
              <Input
                type="text"
                value={profileData.businessInfo.address.line1}
                onChange={(e) => handleAddressChange('line1', e.target.value)}
                placeholder="Street address, P.O. box"
              />
            </InputGroup>
            <InputGroup>
              <Label>Address line 2 <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.6)' }}>(optional)</span></Label>
              <Input
                type="text"
                value={profileData.businessInfo.address.line2}
                onChange={(e) => handleAddressChange('line2', e.target.value)}
                placeholder="Suite, unit, floor"
              />
            </InputGroup>
            <GridRow>
              <InputGroup>
                <Label>City</Label>
                <Input type="text" value={profileData.businessInfo.address.city}
                  onChange={(e) => handleAddressChange('city', e.target.value)} />
              </InputGroup>
              <InputGroup>
                <Label>Province / State</Label>
                <Input type="text" value={profileData.businessInfo.address.province}
                  onChange={(e) => handleAddressChange('province', e.target.value)} />
              </InputGroup>
            </GridRow>
            <GridRow>
              <InputGroup>
                <Label>Postal / ZIP code</Label>
                <Input type="text" value={profileData.businessInfo.address.postalCode}
                  onChange={(e) => handleAddressChange('postalCode', e.target.value)} />
              </InputGroup>
              <InputGroup>
                <Label>Country</Label>
                <Input type="text" value={profileData.businessInfo.address.country}
                  onChange={(e) => handleAddressChange('country', e.target.value)} placeholder="e.g., Canada" />
              </InputGroup>
            </GridRow>
          </ContentCard>

          {/* Structured working hours — per weekday, shown to connected advisers */}
          <ContentCard>
            <CardTitle><BsClock /> Working Hours</CardTitle>
            <HelpText>Set your open hours per day. Toggle "Closed" for days you don't work. Shown to connected advisers.</HelpText>
            <InputGroup>
              <Label>Timezone</Label>
              <Input
                type="text"
                value={profileData.businessInfo.workingHours.timezone}
                onChange={(e) => handleWorkingHoursTz(e.target.value)}
                placeholder="e.g., America/Toronto"
              />
            </InputGroup>
            {WEEKDAYS.map(({ key, label }) => {
              const day = profileData.businessInfo.workingHours.days[key] || {};
              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', flexWrap: 'wrap' }}>
                  <span style={{ width: '96px', fontWeight: 500, color: 'rgba(255,255,255,0.9)' }}>{label}</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'rgba(255,255,255,0.8)' }}>
                    <input
                      type="checkbox"
                      checked={!!day.closed}
                      onChange={(e) => handleWorkingDayChange(key, 'closed', e.target.checked)}
                    />
                    Closed
                  </label>
                  {!day.closed && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Input
                        type="time"
                        value={day.open || ''}
                        onChange={(e) => handleWorkingDayChange(key, 'open', e.target.value)}
                        style={{ width: '140px' }}
                      />
                      <span style={{ color: 'rgba(255,255,255,0.7)' }}>to</span>
                      <Input
                        type="time"
                        value={day.close || ''}
                        onChange={(e) => handleWorkingDayChange(key, 'close', e.target.value)}
                        style={{ width: '140px' }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </ContentCard>

          {/* Specialized-tools status — placeholder for disciplines without a toolset yet */}
          <ContentCard>
            <CardTitle><BsGear /> {capContent.toolsetLabel || 'Specialized tools'}</CardTitle>
            {hasAnySpecializedTool ? (
              <HelpText>
                Your discipline ({capContent.domainName}) has a specialized toolset enabled.
                Its tools appear across the dashboard (forms, programs, firm standards, etc.).
              </HelpText>
            ) : (
              <HelpText>
                No discipline-specific toolset is enabled for your category yet. The general
                tools — conversations, meetings, documents, payments and e-signatures — are all
                active. Specialized tools for your discipline will appear here as we add them.
              </HelpText>
            )}
          </ContentCard>
          </>
        );

      case 'contact':
        return (
          <ContentCard>
            <CardTitle><BsEnvelope /> Contact Information</CardTitle>
            <InputGroup>
              <Label>Email</Label>
              <Input
                type="email"
                value={profileData.contact.email}
                onChange={(e) => handleInputChange('contact', 'email', e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Phone</Label>
              <Input
                type="tel"
                value={profileData.contact.phone}
                onChange={(e) => handleInputChange('contact', 'phone', e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Position</Label>
              <Input
                type="text"
                value={profileData.contact.position}
                onChange={(e) => handleInputChange('contact', 'position', e.target.value)}
                placeholder="e.g., CEO, Consultant"
              />
            </InputGroup>
          </ContentCard>
        );

      case 'social':
        return (
          <ContentCard>
            <CardTitle><BsShare /> Social Media</CardTitle>
            <InputGroup>
              <Label>LinkedIn URL</Label>
              <Input
                type="url"
                value={profileData.socialMedia.linkedin}
                onChange={(e) => handleInputChange('socialMedia', 'linkedin', e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Facebook URL</Label>
              <Input
                type="url"
                value={profileData.socialMedia.facebook}
                onChange={(e) => handleInputChange('socialMedia', 'facebook', e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>X (Twitter) URL</Label>
              <Input
                type="url"
                value={profileData.socialMedia.twitter}
                onChange={(e) => handleInputChange('socialMedia', 'twitter', e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Instagram URL</Label>
              <Input
                type="url"
                value={profileData.socialMedia.instagram}
                onChange={(e) => handleInputChange('socialMedia', 'instagram', e.target.value)}
              />
            </InputGroup>
          </ContentCard>
        );

      case 'api':
        return (
          <ContentCard>
            <CardTitle><BsKey /> API Key</CardTitle>
            <InputGroup>
              <Label>Your API Key</Label>
              <FieldRow>
                <Input type="text" value={profileData.apiKey || ''} readOnly placeholder="No API key yet" />
                <ActionButton onClick={() => copyToClipboard(profileData.apiKey, 'API key')} disabled={!profileData.apiKey}>
                  Copy
                </ActionButton>
              </FieldRow>
              {!profileData.apiKey && (
                <HelpText>No API key is linked yet. Complete onboarding or ask support to generate a key.</HelpText>
              )}
            </InputGroup>
          </ContentCard>
        );

      case 'crm':
        return (
          <ContentCard>
            <CardTitle><BsLink45Deg /> CRM Integration</CardTitle>
            <CRMIntegration />
          </ContentCard>
        );

      case 'calendar':
        return (
          <ContentCard>
            <CardTitle><BsCalendar3 /> Calendar Integration</CardTitle>
            <GoogleCalendarIntegration />
          </ContentCard>
        );

      case 'esignature':
        return (
          <ContentCard>
            <CardTitle><BsPen /> E-Signature Integration</CardTitle>
            <DocuSignIntegration />
            <SignatureTemplates />
          </ContentCard>
        );

      case 'ai':
        return (
          <>
          <ContentCard>
            <CardTitle><BsRobot /> AI Model Configuration</CardTitle>
            <AIModelConfig />
          </ContentCard>
          <ContentCard style={{ marginTop: 24 }}>
            <CardTitle><BsRobot /> Brain Auto-Learning</CardTitle>
            <AutoReviewToggle apiClient={apiClient} />
          </ContentCard>
          </>
        );

      case 'widget-access':
        return (
          <ContentCard>
            <CardTitle><BsGlobe /> Share Your Widget</CardTitle>
            <InputGroup>
              <Label>Shareable URL</Label>
              <FieldRow>
                <Input type="text" value={getWidgetUrl()} readOnly placeholder="Link will appear when an API key is available" />
                <ActionButton onClick={() => copyToClipboard(getWidgetUrl(), 'Widget URL')} disabled={!profileData.apiKey}>
                  Copy
                </ActionButton>
                <ActionButton onClick={shareWidgetUrl} disabled={!profileData.apiKey}>
                  Share
                </ActionButton>
              </FieldRow>
              {profileData.apiKey && (
                <div style={{ marginTop: '12px' }}>
                  <a 
                    href={getWidgetUrl()} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={{ color: '#818cf8', fontWeight: '500', textDecoration: 'none' }}
                  >
                    Open your widget link →
                  </a>
                </div>
              )}
            </InputGroup>
          </ContentCard>
        );

      case 'widget-embed':
        return (
          <>
            <ContentCard>
              <CardTitle><BsCodeSlash /> Embed on Your Website</CardTitle>
              <InputGroup>
                <Label>Paste this before &lt;/body&gt; on every page</Label>
                <TextArea 
                  readOnly 
                  value={getEmbedSnippet()} 
                  style={{ fontFamily: 'monospace', minHeight: '150px' }}
                />
                <FieldRow style={{ marginTop: '12px' }}>
                  <ActionButton onClick={() => copyToClipboard(getEmbedSnippet(), 'Embed code')} disabled={!profileData.apiKey}>
                    Copy Embed Code
                  </ActionButton>
                  <ActionButton onClick={() => setShowTutorial(true)} disabled={!profileData.apiKey}>
                    View Tutorial
                  </ActionButton>
                </FieldRow>
                {!profileData.apiKey && (
                  <HelpText>Embed code will be generated once an API key is available.</HelpText>
                )}
                {profileData.apiKey && (
                  <HelpText>
                    The last line adds a small "Powered by iMaven" link to {publicPage.listed ? 'your public page' : 'imavenai.com'}; it is optional and you can delete it, the widget works the same.
                    {!publicPage.listed && ' Once your marketplace listing is live, copy the code again so the link points to your own page.'}
                  </HelpText>
                )}
              </InputGroup>
            </ContentCard>

            {showTutorial && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: '#1a1f2e', color: 'white', width: 'min(800px, 92vw)', maxHeight: '85vh', overflowY: 'auto', borderRadius: '16px', padding: '28px', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{ margin: 0, fontSize: '20px' }}>AI Chatbot Integration Tutorial</h2>
                    <ActionButton onClick={() => setShowTutorial(false)}>Close</ActionButton>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, fontSize: '14px' }}>
                    <p>🚀 This guide helps you embed the iMavenAi chatbot on your site.</p>
                    
                    <h3 style={{ marginTop: '20px', color: '#fff' }}>Integration Code</h3>
                    <p>Add this code to your site, replacing <strong>YOUR_API_KEY_HERE</strong> with your key:</p>
                    <pre style={{ background: '#0f1419', padding: '16px', borderRadius: '10px', overflowX: 'auto', fontSize: '13px', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                      <code>{getPlaceholderSnippet()}</code>
                    </pre>

                    <h3 style={{ marginTop: '24px', color: '#fff' }}>Platform Instructions</h3>
                    <div style={{ display: 'grid', gap: '16px' }}>
                      <IntegrationCard>
                        <strong>Webflow:</strong> Project Settings → Custom Code → Before &lt;/body&gt; → Publish
                      </IntegrationCard>
                      <IntegrationCard>
                        <strong>WordPress:</strong> Use "Insert Headers and Footers" plugin or edit footer.php
                      </IntegrationCard>
                      <IntegrationCard>
                        <strong>Shopify:</strong> Online Store → Themes → Edit Code → theme.liquid → Before &lt;/body&gt;
                      </IntegrationCard>
                      <IntegrationCard>
                        <strong>Squarespace:</strong> Settings → Advanced → Code Injection → Footer
                      </IntegrationCard>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        );

      case 'widget-style':
        return (
          <ContentCard>
            <CardTitle><BsPalette /> Widget Customization</CardTitle>
            <InputGroup>
              <Label>Color Scheme</Label>
              <Select
                value={profileData.widget.colorScheme}
                onChange={e => handleInputChange('widget', 'colorScheme', e.target.value)}
              >
                {COLOR_SCHEME_OPTIONS.map(option => (
                  <option key={option} value={option}>{option.charAt(0).toUpperCase() + option.slice(1)}</option>
                ))}
              </Select>
            </InputGroup>
            <InputGroup>
              <Label>Logo URL</Label>
              <Input
                type="url"
                value={profileData.widget.logoUrl}
                onChange={e => handleInputChange('widget', 'logoUrl', e.target.value)}
                placeholder="https://example.com/logo.svg"
              />
            </InputGroup>
          </ContentCard>
        );

      case 'templates':
        return (
          <ContentCard>
            <CardTitle><BsFileText /> Form Templates</CardTitle>
            <DocumentTemplates onViewDetails={(templateId) => navigate(`/settings/form-template/${templateId}`)} />
          </ContentCard>
        );

      case 'ircc':
        return (
          <ContentCard>
            <CardTitle><BsFileText /> 🍁 IRCC Forms Manager</CardTitle>
            <IRCCAdmin />
          </ContentCard>
        );

      case 'team':
        return (
          <ContentCard>
            <CardTitle><BsPeople /> Team Members</CardTitle>
            <TeamManagement />
          </ContentCard>
        );

      case 'meetings':
        return (
          <ContentCard>
            <CardTitle><BsCalendarCheck /> Post-Conversation Settings</CardTitle>
            <ToggleContainer>
              <ToggleLabel>
                <ToggleLabelText>Allow Meetings from Widget</ToggleLabelText>
                <ToggleLabelDescription>
                  When enabled, users can schedule meetings after completing a conversation in the widget.
                </ToggleLabelDescription>
              </ToggleLabel>
              <ToggleSwitch>
                <input
                  type="checkbox"
                  checked={profileData.meetingSettings.allowMeetingsFromWidget}
                  onChange={(e) => handleInputChange('meetingSettings', 'allowMeetingsFromWidget', e.target.checked)}
                />
                <span></span>
              </ToggleSwitch>
            </ToggleContainer>

            <ToggleContainer>
              <ToggleLabel>
                <ToggleLabelText>Allow Manual Meeting Scheduling</ToggleLabelText>
                <ToggleLabelDescription>
                  When enabled, users can manually schedule meetings from the platform.
                </ToggleLabelDescription>
              </ToggleLabel>
              <ToggleSwitch>
                <input
                  type="checkbox"
                  checked={profileData.meetingSettings.allowManualMeetings}
                  onChange={(e) => handleInputChange('meetingSettings', 'allowManualMeetings', e.target.checked)}
                />
                <span></span>
              </ToggleSwitch>
            </ToggleContainer>

            <ToggleContainer>
              <ToggleLabel>
                <ToggleLabelText>Allow AI Overview from Widget</ToggleLabelText>
                <ToggleLabelDescription>
                  When enabled, users can request a first AI-assisted overview of their case after completing a conversation in the widget. The AI assistant will analyze the conversation and provide initial guidance.
                </ToggleLabelDescription>
              </ToggleLabel>
              <ToggleSwitch>
                <input
                  type="checkbox"
                  checked={profileData.meetingSettings.allowAiOverviewFromWidget}
                  onChange={(e) => handleInputChange('meetingSettings', 'allowAiOverviewFromWidget', e.target.checked)}
                />
                <span></span>
              </ToggleSwitch>
            </ToggleContainer>

            {profileData.meetingSettings.allowAiOverviewFromWidget && (
              <ToggleContainer>
                <ToggleLabel>
                  <ToggleLabelText>AI Analysis Price (USD)</ToggleLabelText>
                  <ToggleLabelDescription>
                    Set the price users will pay for the AI case analysis. Enter 0 for free.
                  </ToggleLabelDescription>
                </ToggleLabel>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={profileData.meetingSettings.aiAnalysisPrice ?? 10}
                  onChange={(e) => handleInputChange('meetingSettings', 'aiAnalysisPrice', parseFloat(e.target.value) || 0)}
                  style={{
                    width: '100px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    textAlign: 'center',
                    outline: 'none'
                  }}
                />
              </ToggleContainer>
            )}

            <HelpText>
              These settings control how users can interact with you after a widget conversation — schedule meetings, get AI overviews, or both.
            </HelpText>
          </ContentCard>
        );

      case 'marketplace':
        return (
          <ContentCard>
            <CardTitle><BsShop /> Marketplace Listing</CardTitle>
            <MarketplaceSettings />
          </ContentCard>
        );

      case 'payments':
        return (
          <ContentCard>
            <CardTitle><BsCreditCard /> Payment Settings</CardTitle>
            <PaymentSettings />
          </ContentCard>
        );

      case 'meeting-reminders':
        return (
          <ContentCard>
            <CardTitle><BsBell /> Meeting Reminders</CardTitle>
            <MeetingReminderSettingsSection 
              apiClient={apiClient}
              success={success}
              showError={showError}
            />
          </ContentCard>
        );

      case 'deadline-reminders':
        return (
          <ContentCard>
            <CardTitle><BsClock /> Deadline Reminders</CardTitle>
            <DeadlineReminderSettings />
          </ContentCard>
        );

      case 'preferences':
        return (
          <ContentCard>
            <CardTitle><BsGear /> Preferences</CardTitle>
            <InputGroup>
              <Label>Language</Label>
              <LanguageSelector
                language={profileData.preferences.language}
                onChange={(newLanguage) => handleInputChange('preferences', 'language', newLanguage)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Timezone</Label>
              <Input
                type="text"
                value={profileData.preferences.timezone}
                onChange={(e) => handleInputChange('preferences', 'timezone', e.target.value)}
              />
            </InputGroup>
          </ContentCard>
        );

      default:
        return <ContentCard><p>Select a section from the sidebar</p></ContentCard>;
    }
  };

  // Loading state
  if (isLoading || loading) {
    return (
      <PageWrapper style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(99, 102, 241, 0.2)',
            borderTop: '4px solid #6366f1',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <h2 style={{ color: '#fff', margin: 0 }}>Loading Profile...</h2>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </PageWrapper>
    );
  }

  // Error state
  if (error) {
    return (
      <PageWrapper style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: '500px', padding: '40px' }}>
          <h2 style={{ color: '#fff', marginBottom: '16px' }}>Error Loading Profile</h2>
          <p style={{ color: '#ff6b6b', marginBottom: '24px' }}>{error}</p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <ActionButton $primary onClick={() => window.location.reload()}>Retry</ActionButton>
            <ActionButton onClick={() => window.location.href = window.location.pathname + '?mock=true'}>
              Use Mock Data
            </ActionButton>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <ProfileAssistantProvider>
      <PageWrapper>
        {/* Left Sidebar Navigation */}
        <LeftSidebar>
          <SidebarHeader>
            <ProfileCard>
              {(user?.picture || profileData.businessInfo.imageUrl) ? (
                <AvatarImage src={user?.picture || profileData.businessInfo.imageUrl} alt="Profile" />
              ) : (
                <AvatarFallback>{(user?.name || user?.email || 'U').slice(0, 2).toUpperCase()}</AvatarFallback>
              )}
              <UserDetails>
                <UserName>{profileData.businessInfo.businessName || user?.name || 'User'}</UserName>
                <UserEmail>{user?.email}</UserEmail>
              </UserDetails>
            </ProfileCard>
          </SidebarHeader>

          {visibleNavSections.map((section, idx) => (
            <NavSection key={idx}>
              <NavSectionTitle>{section.title}</NavSectionTitle>
              {section.items.map(item => (
                <NavItem
                  key={item.id}
                  $active={activeSection === item.id}
                  onClick={() => setActiveSection(item.id)}
                >
                  <NavIcon $active={activeSection === item.id}>
                    <item.icon />
                  </NavIcon>
                  <NavLabel $active={activeSection === item.id}>{item.label}</NavLabel>
                  {item.badge && <NavBadge>{item.badge}</NavBadge>}
                </NavItem>
              ))}
            </NavSection>
          ))}
        </LeftSidebar>

        {/* Main Content Area */}
        <MainContent>
          <ContentHeader>
            <ContentTitle>
              {NAV_SECTIONS.flatMap(s => s.items).find(i => i.id === activeSection)?.icon && 
                React.createElement(NAV_SECTIONS.flatMap(s => s.items).find(i => i.id === activeSection).icon)}
              {getCurrentSectionTitle()}
            </ContentTitle>
            <ContentSubtitle>{SectionDescriptions[activeSection]}</ContentSubtitle>
          </ContentHeader>

          <ContentBody>
            {renderSectionContent()}
          </ContentBody>

          <StickyFooter>
            <ActionButton $primary onClick={handleSave}>
              Save Changes
            </ActionButton>
          </StickyFooter>
        </MainContent>

        {/* Right Toolbar & Profile Assistant Panel */}
        <ProfileToolbarWrapper />
        <ProfileAssistantPanel />
      </PageWrapper>
    </ProfileAssistantProvider>
  );
};

// Toolbar wrapper component
const ProfileToolbarWrapper = () => {
  const { togglePanel, isPanelOpen, isPinned, conversationHistory } = useProfileAssistant();
  
  return (
    <RightToolbar 
      isPanelPinned={isPinned}
      isAssistantOpen={isPanelOpen}
      onToggleAssistant={togglePanel}
      hasNewMessages={conversationHistory.length === 0}
    />
  );
};

export default ProfilePage;
